<template>
    <div class="update-prompt" v-if="visible">
        <span class="update-prompt__text">{{ text }}</span>
        <span class="update-prompt__action" v-on:click="reload">{{ actionText }}</span>
        <i class="material-icons update-prompt__close" v-on:click="dismiss">close</i>
    </div>
</template>

<script>
    /*
     * The platform is a PWA. Workbox installs a new service worker but leaves it waiting until
     * every tab for the origin is closed, so a returning visitor keeps the previously cached
     * bundle and a deploy can look like it never happened. registerServiceWorker.js raises a
     * `sw:updated` event when a new worker is waiting; this offers the reload rather than taking
     * it, so nobody loses a half-filled order ticket to a surprise refresh.
     *
     * The waiting worker only reacts to SKIP_WAITING if the copy the visitor already has
     * installed carries that listener, so this takes effect from the update after the one that
     * ships it.
     */
    export default {
        name: "UpdatePrompt",
        data() {
            return {
                visible: false,
                registration: null,
                reloading: false
            }
        },
        computed: {
            text() {
                return (this.$store.getters.tokens
                    && this.$store.getters.tokens.updatePrompt
                    && this.$store.getters.tokens.updatePrompt.newVersion) || 'A new version is available.'
            },
            actionText() {
                return (this.$store.getters.tokens
                    && this.$store.getters.tokens.updatePrompt
                    && this.$store.getters.tokens.updatePrompt.reload) || 'Reload'
            }
        },
        methods: {
            onUpdated(e) {
                this.registration = e.detail
                this.visible = true
            },
            dismiss() {
                this.visible = false
            },
            reload() {
                const waiting = this.registration && this.registration.waiting
                if (!waiting) {
                    window.location.reload()
                    return
                }
                /* controllerchange fires once the waiting worker takes over; reload only then,
                   and only once, or Chrome will loop the page. */
                navigator.serviceWorker.addEventListener('controllerchange', () => {
                    if (this.reloading) {
                        return
                    }
                    this.reloading = true
                    window.location.reload()
                })
                waiting.postMessage({type: 'SKIP_WAITING'})
                this.visible = false
            }
        },
        mounted() {
            window.addEventListener('sw:updated', this.onUpdated)
        },
        beforeDestroy() {
            window.removeEventListener('sw:updated', this.onUpdated)
        }
    }
</script>

<style scoped>
    .update-prompt {
        position: fixed;
        left: 50%;
        bottom: 16px;
        transform: translateX(-50%);
        z-index: 100000;
        display: flex;
        align-items: center;
        gap: 14px;
        max-width: calc(100vw - 24px);
        padding: 10px 12px 10px 16px;
        border: 1px solid var(--border);
        border-radius: 6px;
        background: var(--background-dark);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.45);
        font-size: 13px;
        color: var(--white-to-black);
    }

    .update-prompt__text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .update-prompt__action {
        cursor: pointer;
        white-space: nowrap;
        font-weight: 600;
        color: var(--brand);
    }

    .update-prompt__close {
        cursor: pointer;
        font-size: 18px;
        opacity: 0.7;
    }
</style>
